// UnitGroupPermissionDialog - MODULE
// ***********************************************************************************************************************
import * as React from "react";
import { ConfirmationDialogResult, EnumHelper, GroupPermissionLevel } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../../framework/appUtils";
import { SaveDataObj } from "../../../../framework/saveData";
import { IUnitNode } from "../../../../models/unit/unitNode";
import { IUnitPermissionUserGroup } from "../../../../models/userGroup/unitPermissionUserGroup";
import { Dialog } from "../../Dialog/Dialog";
import { Box, Checkbox, FormControlLabel, Grid, Radio, Stack } from "@mui/material";
import { CheckBox } from "ui/Scripts/source/components/framework/checkbox";
import { ICompanyListItem } from "../../../../models/company/companyListItem";
import { GridRow } from "../../Grid/GridRow";
import { PanelTitle } from "../../Panel/PanelTitle";
import { UnitGroupPermissionDialogPartnerCompanyList } from "../UnitGroupPermissionDialogPartnerCompanyList";

//UnitGroupPermissionDialog
export interface IUnitGroupPermissionDialogProp {
    classes?: string;
    unitNode: IUnitNode;
    companyGroup: IUnitPermissionUserGroup;
    userGroup: IUnitPermissionUserGroup;
    accessRightNoneTitle: string;
    accessRightReadTitle: string;
    accessRightWriteTitle: string;
    additionalPermissionTitle: string[];
    partnerCompaniesTitle?: string;
    partnerCompanies?: ICompanyListItem[];
    getAdditionalPermissionInUse: (permission: number, additionalPermissionIndex: number) => boolean;
    onOk: (permission: number, additionalPermissionA: number, additionalPermissionB: number, additionalPermissionC: number, clearSubUnitPermissions: boolean, copyPermissionToAllSameLevelUnits: boolean, partnerIds: string[]) => void;
    onRemove: () => void;
    onCancel: () => void;
}

export interface IUnitGroupPermissionDialogState {
    unitPartnerCompanies: ICompanyListItem[];
    partnerCompanies: ICompanyListItem[];
    permission: number;
    nonExplicitPermission: number;
    additionalPermission: number[];
    hasSiblings: boolean;
    clearSubUnitPermissions: boolean;
    copyPermissionToAllSameLevelUnits: boolean;
}

export class UnitGroupPermissionDialog extends React.Component<IUnitGroupPermissionDialogProp, IUnitGroupPermissionDialogState> {
    private orgStateHash: string = "";

    constructor(props: IUnitGroupPermissionDialogProp) {
        super(props);
        const nonExplicitGroupPermission = props.unitNode.getEffectiveNonExplicitPermission(props.companyGroup, props.userGroup);
        const effectiveUnitNodeCompanyIds = props.unitNode.getEffectiveUnitNodeCompanyIds(props.userGroup);
        this.state = {
            unitPartnerCompanies: (props.partnerCompanies ?? []).filter(i => props.unitNode.partnerCompanyIds.indexOf(i.id) > -1),
            permission: Math.min(props.userGroup ? props.unitNode.permission.permission : props.unitNode.permission.companyGroupPermission, Math.min(props.unitNode.maxPermissionLevel, GroupPermissionLevel.Write)),
            nonExplicitPermission: Math.min(props.userGroup ? nonExplicitGroupPermission.permission : nonExplicitGroupPermission.companyGroupPermission, GroupPermissionLevel.Write),
            additionalPermission: [
                Math.min(props.userGroup ? props.unitNode.permission.additionalPermissionA : props.unitNode.permission.companyGroupAdditionalPermissionA, Math.min(props.unitNode.maxPermissionLevelA, 1)),
                Math.min(props.userGroup ? props.unitNode.permission.additionalPermissionB : props.unitNode.permission.companyGroupAdditionalPermissionB, Math.min(props.unitNode.maxPermissionLevelB, 1)),
                Math.min(props.userGroup ? props.unitNode.permission.additionalPermissionC : props.unitNode.permission.companyGroupAdditionalPermissionC, Math.min(props.unitNode.maxPermissionLevelC, 1))
            ],
            partnerCompanies: (props.partnerCompanies ?? []).filter(i => effectiveUnitNodeCompanyIds.companyIds.indexOf(i.id) > -1),
            hasSiblings: !props.unitNode.getIsRoot() && props.unitNode.hasSiblings(),
            clearSubUnitPermissions: false,
            copyPermissionToAllSameLevelUnits: false
        };
        const saveData = this.getSaveDataFromState(props, this.state);
        this.orgStateHash = saveData.hash;
    }

    // #region Partner Companies
    handleCompaniesRemoved = (ids: string[]) => {
        if (!ids || ids.length < 1) return;
        this.setState({
            partnerCompanies: this.state.partnerCompanies.filter(i => ids.indexOf(i.id) < 0)
        });
    };
    
    handleCompaniesAdded = (newCompanies: ICompanyListItem[]) => {
        const state = this.state;
        this.setState({
            partnerCompanies: state.partnerCompanies.filter(i => newCompanies.findIndex(j => j.id === i.id) < 0).concat(newCompanies)
        });
    };
    // #endregion Partner Companies

    handleSetPermission = (checked: boolean, permission: number) => {
        if (!checked) return;
        this.setState({
            permission
        });
    };

    handleSetAdditionalPermission = (checked: boolean, additionalPermissionIndex: number) => {
        const additionalPermission = this.state.additionalPermission.slice(0);
        additionalPermission[additionalPermissionIndex] = checked ? 1 : 0;
        this.setState({
            additionalPermission
        });
    };

    getAdditionalPermissionInUse = (additionalPermissionIndex: number): boolean => {
        return this.props.getAdditionalPermissionInUse(this.state.permission, additionalPermissionIndex);
    };

    handleSetClearSubUnitPermissions = (checked: boolean) => {
        this.setState({
            clearSubUnitPermissions: checked
        });
    };

    handleSetCopyPermissionToAllSameLevelUnits = (checked: boolean) => {
        this.setState({
            copyPermissionToAllSameLevelUnits: checked
        });
    };

    getSaveDataFromState = (props: IUnitGroupPermissionDialogProp, state: IUnitGroupPermissionDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("permission", state.permission);
        result.append("additionalPermissionA", state.additionalPermission[0]);
        result.append("additionalPermissionB", state.additionalPermission[1]);
        result.append("additionalPermissionC", state.additionalPermission[2]);
        result.append("partnerCompanyIds", state.partnerCompanies.map(i => i.id));
        result.append("clearSubUnitPermissions", state.clearSubUnitPermissions);
        result.append("copyPermissionToAllSameLevelUnits", state.copyPermissionToAllSameLevelUnits);
        return result;
    };

    save = async () => {
        const state = this.state;
        this.props.onOk(
            state.permission,
            this.getAdditionalPermissionInUse(0) ? state.additionalPermission[0] : 0,
            this.getAdditionalPermissionInUse(1) ? state.additionalPermission[1] : 0,
            this.getAdditionalPermissionInUse(2) ? state.additionalPermission[2] : 0,
            state.clearSubUnitPermissions,
            state.copyPermissionToAllSameLevelUnits,
            state.partnerCompanies.map(i => i.id)
        );
    };

    handleSave = () => {
        this.save();
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.save();
        } else {
            props.onCancel();
        }
    };

    render() {
        const props = this.props;
        const state = this.state;
        const additionalPermissionAInUse = this.getAdditionalPermissionInUse(0);
        const additionalPermissionBInUse = this.getAdditionalPermissionInUse(1);
        const additionalPermissionCInUse = this.getAdditionalPermissionInUse(2);
        return (
            <Dialog
                id="unit-group-permission-dialog"
                title={props.unitNode.name}
                body={
                    <Box sx={{ margin: "0 0 -1rem 0" }}>
                        <GridRow>
                            <Grid item sm={12}>
                                <PanelTitle title={Translations.Permission} />
                                <Stack>
                                    {!!props.accessRightNoneTitle && EnumHelper.isGreaterOrEqualThan(props.unitNode.maxPermissionLevel, GroupPermissionLevel.None) &&
                                        <FormControlLabel
                                            label={props.accessRightNoneTitle}
                                            control={
                                                <Radio
                                                    checked={EnumHelper.isEqual(state.permission, GroupPermissionLevel.None)}
                                                    onChange={(_e, checked: boolean) => this.handleSetPermission(checked, GroupPermissionLevel.None)}
                                                />
                                            }
                                            sx={{ marginBottom: 0 }}
                                        />
                                    }
                                    {!!props.accessRightReadTitle && EnumHelper.isGreaterOrEqualThan(props.unitNode.maxPermissionLevel, GroupPermissionLevel.Read) &&
                                        <FormControlLabel
                                            label={props.accessRightReadTitle}
                                            control={
                                                <Radio
                                                    checked={EnumHelper.isEqual(state.permission, GroupPermissionLevel.Read)}
                                                    onChange={(_e, checked: boolean) => this.handleSetPermission(checked, GroupPermissionLevel.Read)}
                                                />
                                            }
                                            sx={{ marginBottom: 0 }}
                                        />
                                    }
                                    {!!props.accessRightWriteTitle && EnumHelper.isGreaterOrEqualThan(props.unitNode.maxPermissionLevel, GroupPermissionLevel.Write) &&
                                        <FormControlLabel
                                            label={props.accessRightWriteTitle}
                                            control={
                                                <Radio
                                                    checked={EnumHelper.isEqual(state.permission, GroupPermissionLevel.Write)}
                                                    onChange={(_e, checked: boolean) => this.handleSetPermission(checked, GroupPermissionLevel.Write)}
                                                />
                                            }
                                            sx={{ marginBottom: 0 }}
                                        />
                                    }
                                </Stack>
                            </Grid>
                            {(additionalPermissionAInUse || additionalPermissionBInUse || additionalPermissionCInUse) &&
                                <Grid item sm={12}>
                                    <PanelTitle title={Translations.AdditionalPermissions} />
                                    {additionalPermissionAInUse &&
                                        <FormControlLabel
                                            label={props.additionalPermissionTitle[0]}
                                            control={
                                                <Checkbox
                                                    checked={state.additionalPermission[0] > 0}
                                                    onChange={(_e, checked: boolean) => this.handleSetAdditionalPermission(checked, 0)}
                                                />
                                            }
                                        />
                                    }
                                    {additionalPermissionBInUse &&
                                        <FormControlLabel
                                            label={props.additionalPermissionTitle[1]}
                                            control={
                                                <Checkbox
                                                    checked={state.additionalPermission[1] > 0}
                                                    onChange={(_e, checked: boolean) => this.handleSetAdditionalPermission(checked, 1)}
                                                />
                                            }
                                        />
                                    }
                                    {additionalPermissionCInUse &&
                                        <CheckBox
                                            noPadding={true}
                                            title={props.additionalPermissionTitle[2]}
                                            checked={state.additionalPermission[2] > 0}
                                            onClickBoolean={(checked: boolean) => this.handleSetAdditionalPermission(checked, 2)}
                                        />
                                    }
                                    {(state.unitPartnerCompanies.length > 0 || state.partnerCompanies.length > 0) &&
                                        <UnitGroupPermissionDialogPartnerCompanyList
                                            availableCompanies={state.unitPartnerCompanies}
                                            companies={state.partnerCompanies}
                                            title={props.partnerCompaniesTitle ?? Translations.Partners}
                                            onRemoved={this.handleCompaniesRemoved}
                                            onAdded={this.handleCompaniesAdded}
                                        />
                                    }
                                </Grid>
                            }
                            <Grid item sm={12}>
                                {(props.unitNode.hasChildren() || state.hasSiblings) &&
                                    <PanelTitle title={Translations.AdditionalSettings} />
                                }
                                {props.unitNode.hasChildren() &&
                                    <FormControlLabel
                                        label={Translations.ClearSubUnitPermissions}
                                        control={
                                            <Checkbox
                                                checked={state.clearSubUnitPermissions}
                                                onChange={(_e, v) => this.handleSetClearSubUnitPermissions(v)}
                                            />
                                        }
                                    />
                                }
                                {state.hasSiblings &&
                                    <FormControlLabel
                                        label={Translations.CopyPermissionToAllSameLevelUnits}
                                        control={
                                            <Checkbox
                                                checked={state.copyPermissionToAllSameLevelUnits}
                                                onChange={(_e, v) => this.handleSetCopyPermissionToAllSameLevelUnits(v)}
                                            />
                                        }
                                    />
                                }
                            </Grid>
                        </GridRow>
                    </Box>
                }
                buttons={[
                    { title: Translations.Remove, variant: "danger", onClick: props.unitNode.hasExplicitPermission(null, props.userGroup.code) && !(props.unitNode.getIsRoot() && !props.userGroup.code) ? props.onRemove : null },
                    { title: Translations.Close, variant: "outlined", onClick: this.handleCancel },
                    { title: Translations.OK, variant: "confirmation", onClick: this.handleSave },
                ]}
                onClose={this.handleCancel}/>
        );
    }
}